import React from 'react';
import { Link } from 'react-router-dom';
import workflowImg from '../assets/dworkflow.png';
import integratedImagingImg from '../assets/3dmage.png';
import complianceImg from '../assets/complicance.png';

const features = [
  {
    title: "Cloud-Based Platform",
    description: "Access your dental practice management system from anywhere, anytime with our secure cloud infrastructure.",
    icon: "☁️",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&auto=format&fit=crop&q=60"
  },
  {
    title: "AI-Powered Analytics",
    description: "Leverage advanced analytics and AI to gain insights into your practice performance and patient care.",
    icon: "🤖",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop&q=60"
  },
  {
    title: "Seamless Integration",
    description: "Connect with your favorite dental tools and software through our extensive integration network.",
    icon: "🔄",
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&auto=format&fit=crop&q=60"
  }
];

const benefits = [
  {
    title: "Enhanced Efficiency",
    description: "Streamline your workflow and reduce administrative tasks with automated processes.",
    icon: "⚡"
  },
  {
    title: "Improved Patient Care",
    description: "Deliver better patient outcomes with comprehensive treatment planning and monitoring tools.",
    icon: "❤️"
  },
  {
    title: "Data Security",
    description: "Rest easy knowing your practice and patient data is protected with enterprise-grade security.",
    icon: "🔒"
  },
  {
    title: "Scalable Solution",
    description: "Grow your practice with a platform that scales with your needs.",
    icon: "📈"
  }
];

const integrations = [
  {
    name: "Dental Imaging Systems",
    icon: "📷",
    description: "Connect with major dental imaging systems for seamless workflow"
  },
  {
    name: "Patient Communication",
    icon: "💬",
    description: "Integrated messaging and appointment reminders"
  },
  {
    name: "Insurance Processing",
    icon: "📋",
    description: "Automated insurance verification and claims processing"
  },
  {
    name: "Payment Gateways",
    icon: "💳",
    description: "Secure payment processing and financial management"
  },
  {
    name: "Lab Management",
    icon: "🔬",
    description: "Streamlined communication with dental laboratories"
  },
  {
    name: "Inventory Systems",
    icon: "📦",
    description: "Real-time inventory tracking and management"
  },
  {
    name: "Accounting Software",
    icon: "📊",
    description: "Seamless financial data synchronization"
  },
  {
    name: "Marketing Platforms",
    icon: "📢",
    description: "Integrated marketing and patient engagement tools"
  }
];

const Platform = () => {
  return (
    <div className="pt-24 bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E5F0F3] to-white">
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#1E2A4A] mb-6">
              AI-Powered Dental Platform
            </h1>
            <p className="text-xl text-gray-600">
              A comprehensive, cloud-based platform with advanced AI capabilities designed to transform your dental practice management
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300"
            >
              <div className="relative h-48">
                <img 
                  src={feature.image} 
                  alt={feature.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 right-4 w-12 h-12 bg-white rounded-full flex items-center justify-center text-2xl shadow-lg">
                  {feature.icon}
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-[#1E2A4A] mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#1E2A4A] text-center mb-12">
            Platform Benefits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-gray-50 p-6 rounded-xl text-center hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-3xl mx-auto mb-4 shadow-md">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-[#1E2A4A] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Integrations Section */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-[#1E2A4A] text-center mb-4">
            Seamless Integrations
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Connect with your favorite tools and services to create a unified dental practice ecosystem
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {integrations.map((integration, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                <div className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {integration.icon}
                  </div>
                  <h3 className="text-lg font-bold text-[#1E2A4A] mb-2">
                    {integration.name}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {integration.description}
                  </p>
                </div>
                <div className="h-1 bg-gradient-to-r from-orange-500 to-orange-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link 
              to="/integrations"
              className="inline-flex items-center text-orange-500 font-semibold hover:text-orange-600 transition-colors"
            >
              View All Integrations
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-[#1E2A4A] text-white py-16">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Transform Your Practice?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Experience the power of our modern dental platform
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/schedule-demo"
              className="inline-block bg-orange-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-orange-600 transition-colors"
            >
              Schedule a Demo
            </Link>
            <Link 
              to="/pricing"
              className="inline-block bg-white text-[#1E2A4A] px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Platform; 